import React, { memo, useEffect, useState } from 'react';
import { Form, message } from 'antd';
import axios from '@axios';
import { FormItem, Select, Option } from '@styles/components/form';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '@components/i18n';
import { Modal } from '@components/modal';
import { useQuery } from 'react-query';
import { TypeResponseApi } from '@models/order/type/response';
import { fetchAllAvailablePlatforms, orderTypeSelector } from '@redux/slices/order';
import {
    closeTransferModalBranch,
    transferOrderSelector,
    updateTransferBranch,
    updateTransferUpdateDeliveryClientCode,
    updateTransferUpdateShowDeliveryClientModal,
} from '@redux/slices/transfer-order/order';
import { updateTransferPlatform } from '@redux/slices/transfer-order/select-type';
import { Card } from '@styles/components/wrapper';
import { CustomerResponse } from '@models/customer';
import { Col, Row, Typography } from 'antd';

interface ModalSelectBranchTransferProps {
    isVisible: boolean;
    selectedCustomer: CustomerResponse;
    onClose: () => void;
}

export const ModalSelectBranchTransfer: React.FC<ModalSelectBranchTransferProps> = memo(({
    isVisible,
    selectedCustomer,
    onClose,
}) => {
    const dispatch = useDispatch();
    const transferOrder = useSelector(transferOrderSelector);
    const selectTypeState = useSelector(orderTypeSelector);

    const [form] = Form.useForm();
    const [formState, setFormState] = useState<{ platform?: string }>({
        platform: transferOrder?.filial?.id,
    });

    const { data, refetch } = useQuery(
        ['transfer-branch-suggestion', selectedCustomer?.code],
        async () => {
            if (!selectedCustomer?.code || !selectedCustomer?.company) {
                throw new Error();
            }

            const result = await axios.get<TypeResponseApi>(
                `/filial/byusuario/cliente/${selectedCustomer.code}?companhiaCodigo=${selectedCustomer.company}`,
            );

            const data = Object.assign(new TypeResponseApi(), result.data);
            return data.toTypeResponse();
        },
        { enabled: isVisible && !!selectedCustomer?.code },
    );

    useEffect(() => {
        if (isVisible) {
            dispatch(fetchAllAvailablePlatforms());
        }
    }, [isVisible]);

    useEffect(() => {
        if (data && selectTypeState.allAvailablePlatforms.length > 0) {
            form.setFieldsValue({ platform: data.id });
            setFormState({ platform: data.id });
        } else if (isVisible) {
            refetch();
        }
    }, [data, selectTypeState.allAvailablePlatforms]);

    useEffect(() => {
        if (
            !selectTypeState.isFetchingPlatform &&
            selectTypeState.isSuccessPlatform &&
            selectTypeState.allAvailablePlatforms.length === 0
        ) {
            message.error(translate('general.filialAvailableError'), 3);
        }
    }, [selectTypeState.isFetchingPlatform, selectTypeState.isSuccessPlatform]);

    const onChange = (_, allValues: any): void => {
        setFormState((prev) => ({ ...prev, ...allValues }));
    };

    const onSubmit = (): void => {
        const platformId = formState.platform;

        if (!platformId) {
            message.error(translate('general.filialAvailableError'), 3);
            return;
        }

        const platform = selectTypeState.allAvailablePlatforms.find(
            (i) => i.id === platformId,
        );

        if (!platform) {
            message.error(translate('general.filialAvailableError'), 3);
            return;
        }

        // Salvar o cliente entrega
        dispatch(updateTransferUpdateDeliveryClientCode(selectedCustomer));

        // Salvar a filial selecionada
        dispatch(updateTransferPlatform(platform));
        dispatch(updateTransferBranch({ filial: platform }));

        // Fechar modais
        dispatch(closeTransferModalBranch());
        dispatch(updateTransferUpdateShowDeliveryClientModal(false));
    };

    const handleClose = (): void => {
        dispatch(closeTransferModalBranch());
        onClose();
    };

    return (
        <Modal
            title={translate('general.changeBranch')}
            className={'bold-title'}
            visible={isVisible}
            width={'30%'}
            onOk={onSubmit}
            onCancel={handleClose}
        >
            <Card
                title={
                    <Col>
                        <Row style={{ paddingLeft: 10 }}>
                            <Typography.Text
                                style={{ fontWeight: 300, fontSize: 16 }}
                            >
                                {translate('forms.labels.client')}:
                                <Typography.Text
                                    strong
                                    style={{ marginLeft: 5 }}
                                >
                                    {selectedCustomer?.name}
                                </Typography.Text>
                            </Typography.Text>
                        </Row>
                        <Row style={{ paddingLeft: 10 }}>
                            <Typography.Text
                                style={{ fontWeight: 300, fontSize: 16 }}
                            >
                                {translate('forms.labels.cnpj')}:
                                <Typography.Text
                                    strong
                                    style={{ marginLeft: 5 }}
                                >
                                    {selectedCustomer?.cnpj}
                                </Typography.Text>
                            </Typography.Text>
                        </Row>
                    </Col>
                }
                padding={'24px 15%'}
            >
                <Form
                    form={form}
                    onValuesChange={onChange}
                    layout="vertical"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={formState}
                >
                    <FormItem
                        label={translate('forms.labels.originPlatform')}
                        name={'platform'}
                    >
                        <Select
                            placeholder={translate(
                                'forms.placeholders.chooseOption',
                            )}
                            loading={selectTypeState.isFetchingPlatform}
                            allowClear
                        >
                            {selectTypeState.allAvailablePlatforms.map((t) => (
                                <Option key={t.id} value={t.id}>
                                    {t.id} - {t.description}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                </Form>
            </Card>
        </Modal>
    );
});
